import React, { useState, useEffect } from 'react';
import { useKpros } from '../../context/KprosContext';
import { useTheme } from '../../context/ThemeContext';
import { X, Search, Trash2, Play, BookmarkCheck, ChevronDown } from 'lucide-react';

const FEATURE_OPTIONS = [
  { value: '', label: 'All Prompts' },
  { value: 'chat', label: 'Chat' },
  { value: 'studio', label: 'Studio' },
  { value: 'ait', label: 'AIT' },
  { value: 'data_extraction', label: 'Data Extraction' },
  { value: 'profitability', label: 'Analyzer' },
  { value: 'team_chat', label: 'Team Chat' },
  { value: 'email', label: 'Email' },
];

const FEATURE_LABELS = Object.fromEntries(FEATURE_OPTIONS.map(f => [f.value, f.label]));

export default function SavedPromptsModal({ isOpen, onClose, onUse, currentFeature }) {
  const { savedPrompts, savedPromptsLoading, fetchSavedPrompts, deleteSavedPrompt } = useKpros();
  const { theme } = useTheme();
  const [featureFilter, setFeatureFilter] = useState(currentFeature || '');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFeatureFilter(currentFeature || '');
      setSearch('');
      fetchSavedPrompts();
    }
  }, [isOpen, currentFeature, fetchSavedPrompts]);

  if (!isOpen) return null;

  const filtered = savedPrompts.filter(p => {
    if (featureFilter && p.feature_tag !== featureFilter) return false;
    if (search) {
      const term = search.toLowerCase();
      return p.title.toLowerCase().includes(term) || p.content.toLowerCase().includes(term);
    }
    return true;
  });

  // Group by feature when showing all
  const grouped = featureFilter
    ? { [featureFilter || 'all']: filtered }
    : filtered.reduce((acc, p) => {
        const key = p.feature_tag || 'general';
        if (!acc[key]) acc[key] = [];
        acc[key].push(p);
        return acc;
      }, {});

  const handleUse = (prompt) => {
    onUse(prompt.content);
    onClose();
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await deleteSavedPrompt(id);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[70]" onClick={onClose} />
      <div className={`fixed inset-x-4 top-[10%] bottom-[10%] sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[520px] z-[71] flex flex-col rounded-xl shadow-2xl font-rasa ${
        theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <BookmarkCheck size={18} className="text-blue-500" />
            <h3 className="text-sm font-semibold font-unna text-gray-800 dark:text-gray-200">
              Saved Prompts
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search prompts..."
              className={`w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border ${
                theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-gray-50 text-gray-800'
              } focus:outline-none focus:ring-1 focus:ring-blue-500`}
            />
          </div>
          <select
            value={featureFilter}
            onChange={e => setFeatureFilter(e.target.value)}
            className={`text-sm px-2 py-1.5 rounded-lg border ${
              theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-gray-50 text-gray-800'
            } focus:outline-none focus:ring-1 focus:ring-blue-500`}
          >
            {FEATURE_OPTIONS.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Prompts list */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {savedPromptsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-base">
              {savedPrompts.length === 0
                ? 'No saved prompts yet. Send a message and click the bookmark to save it.'
                : 'No prompts match your filter.'}
            </div>
          ) : (
            Object.entries(grouped).map(([groupKey, prompts]) => (
              <div key={groupKey} className="mb-3">
                {!featureFilter && (
                  <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5 px-1 font-medium">
                    {FEATURE_LABELS[groupKey] || groupKey}
                  </p>
                )}
                <div className="space-y-1.5">
                  {prompts.map(prompt => (
                    <div
                      key={prompt.id}
                      onClick={() => handleUse(prompt)}
                      className={`group p-3 rounded-lg border cursor-pointer transition-colors ${
                        theme === 'dark'
                          ? 'border-gray-700 hover:border-blue-600 hover:bg-gray-750'
                          : 'border-gray-100 hover:border-blue-300 hover:bg-blue-50/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                            {prompt.title}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                            {prompt.content}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => handleDelete(e, prompt.id)}
                            className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                          <div className="p-1 text-blue-500" title="Use this prompt">
                            <Play size={13} />
                          </div>
                        </div>
                      </div>
                      {featureFilter === '' && prompt.feature_tag && (
                        <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          {FEATURE_LABELS[prompt.feature_tag] || prompt.feature_tag}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
